<template>
    <div>
        <!-- This template contains our reusable UI elements that will be injected into the modal -->
        <div ref="smartPressContent" class="hidden" style="display: none;">
            <div class="bg-gradient-to-br from-white to-blue-50 p-8 rounded-xl shadow-lg border border-blue-100">
                <!-- <h2 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-400 mb-6">Generate with AI</h2> -->
                
                <div class="flex gap-8 justify-center items-stretch">
                    <div class="left-side w-1/2 p-4">
                        <div class="mb-6">
                            <div class="flex flex-col gap-3">
                                <div class="relative group">
                                    <div class="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-primary rounded-xl blur-sm opacity-20 group-hover:opacity-30 transition-all duration-300"></div>
                                    <textarea
                                        placeholder="Describe the image you want to generate..." 
                                        class="flex-grow py-4 px-5 rounded-xl bg-white/80 backdrop-blur-sm border border-blue-200 shadow-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all w-full smartpress-disabled prompt-area"
                                        id="sp-prompt-input"
                                        rows="8"
                                    ></textarea>
                                    <div class="absolute bottom-3 right-3 text-xs bg-primary hover:bg-primary-focus text-white py-1 px-3 rounded-full flex items-center cursor-pointer shadow-sm transition-all duration-200 hover:shadow-md" 
                                        @click="enhancePrompt"
                                        id="sp-enhance-btn"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span>Enhance</span>
                                    </div>
                                </div>
                                
                                <!-- Model Selector and Generate Button -->
                                <div class="mb-4">
                                    <div class="flex gap-3 justify-between items-end">
                                        <div class="space-y-2 flex-1">
                                            <label for="sp-model-select" class="block text-sm font-medium text-gray-700">Model</label>
                                            <div class="dropdown w-[90%]" id="sp-model-dropdown">
                                                <div tabindex="0" class="btn btn-sm bg-white hover:bg-opacity-90 text-gray-800 border border-blue-200 w-full justify-between rounded-lg shadow-sm">
                                                    <span id="sp-selected-model" class="font-medium">Basic</span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                    </svg>
                                                </div>
                                                <ul tabindex="0" class="dropdown-content menu bg-white rounded-lg z-10 w-full !p-2 shadow-lg border border-blue-100" id="sp-model-dropdown-list">
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="basic" data-label="Basic">Basic</a>
                                                    </li>
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="sdxl" data-label="SDXL">SDXL</a>
                                                    </li>
                                                </ul>
                                            </div>
                                            <select id="sp-model-select" class="hidden">
                                                <option value="basic">Basic (DALL-E 3)</option>
                                                <option value="sdxl">SDXL</option>
                                            </select>
                                        </div>
                                        
                                        <div class="space-y-2 flex-1">
                                            <label for="sp-size-select" class="block text-sm font-medium text-gray-700">Size</label>
                                            <div class="dropdown w-[90%]" id="sp-size-dropdown">
                                                <div tabindex="0" class="btn btn-sm bg-white hover:bg-opacity-90 text-gray-800 border border-blue-200 w-full justify-between rounded-lg shadow-sm">
                                                    <span id="sp-selected-size" class="font-medium">Square (1024x1024)</span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                    </svg>
                                                </div>
                                                <ul tabindex="0" class="dropdown-content menu bg-white rounded-lg z-10 w-full !p-2 shadow-lg border border-blue-100" id="sp-size-dropdown-list">
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="1024x1024" data-label="Square (1024x1024)">Square (1024x1024)</a>
                                                    </li>
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="512x512" data-label="Square (512x512)">Square (512x512)</a>
                                                    </li>
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="1280x768" data-label="Landscape (1280x768)">Landscape (1280x768)</a>
                                                    </li>
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="704x320" data-label="Landscape (704x320)">Landscape (704x320)</a>
                                                    </li>
                                                </ul>
                                            </div>
                                            <select id="sp-size-select" class="hidden">
                                                <option value="1024x1024">Square (1024x1024)</option>
                                                <option value="512x512">Square (512x512)</option>
                                                <option value="1280x768">Landscape (1280x768)</option>
                                                <option value="704x320">Landscape (704x320)</option>
                                            </select>
                                        </div>
                                        
                                        <div class="space-y-2 flex-1">
                                            <label for="sp-format-select" class="block text-sm font-medium text-gray-700">Format</label>
                                            <div class="dropdown w-[90%]" id="sp-format-dropdown">
                                                <div tabindex="0" class="btn btn-sm bg-white hover:bg-opacity-90 text-gray-800 border border-blue-200 w-full justify-between rounded-lg shadow-sm">
                                                    <span id="sp-selected-format" class="font-medium">WebP</span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                    </svg>
                                                </div>
                                                <ul tabindex="0" class="dropdown-content menu bg-white rounded-lg z-10 w-full !p-2 shadow-lg border border-blue-100" id="sp-format-dropdown-list">
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="webp" data-label="WebP">WebP</a>
                                                    </li>
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="jpeg" data-label="JPEG">JPEG</a>
                                                    </li>
                                                    <li class="!text-sm hover:bg-primary hover:text-white rounded-md">
                                                        <a class="!text-gray-700 hover:!text-white" data-value="png" data-label="PNG">PNG</a>
                                                    </li>
                                                </ul>
                                            </div>
                                            <select id="sp-format-select" class="hidden">
                                                <option value="webp">WebP</option>
                                                <option value="jpeg">JPEG</option>
                                                <option value="png">PNG</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    class="btn bg-primary hover:opacity-90 border-0 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 w-full"
                                    id="sp-generate-btn"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Generate Image
                                </button>
                            </div>
                        </div>
                        
                        <div class="bg-blue-50 rounded-lg p-3 border border-blue-100 backdrop-blur-sm">
                            <div class="flex items-center gap-2 mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-xs font-medium text-primary-focus">Beta Feature</span>
                            </div>
                            <p class="text-xs text-gray-600">This feature is in development and will be enhanced with more models and advanced options in future updates.</p>
                        </div>

                    </div>
                    
                    <div class="right-side w-1/2">
                        <div class="bg-white rounded-xl border border-blue-200 shadow-md p-4 h-full flex flex-col">
                            <div id="sp-skeleton" class="rounded-lg overflow-hidden relative h-80 bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center">
                                <div class="animate-pulse flex flex-col items-center">
                                    <div class="rounded-full bg-slate-200 h-16 w-16 mb-4 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div class="text-center text-sm text-slate-400">Your image will appear here</div>
                                </div>
                            </div>
                            <div class="hidden flex-1 flex flex-col" id="sp-result-container">
                                <div class="mb-4 flex-1 flex items-center justify-center relative group">
                                    <div class="absolute inset-0 rounded-lg overflow-hidden zoom-container pointer-events-none opacity-0 group-hover:opacity-100 transform scale-150 transition-opacity duration-300 z-10"></div>
                                    <img class="absolute w-[200%] h-[200%] object-contain left-0 top-0 zoom-image pointer-events-none z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style="transform-origin: 0 0;" id="sp-zoom-img" />
                                    <img class="w-full object-contain max-h-80 rounded-lg cursor-zoom-in hover:opacity-95 transition-opacity" alt="Generated image" id="sp-result-img" />
                                    <div class="absolute top-2 right-2  group-hover:opacity-100 transition-opacity bg-black/60 text-white text-xs py-1 px-2 rounded pointer-events-none">
                                        Hover to zoom
                                    </div>
                                </div>
                                <div class="flex justify-end mt-2">
                                    <button class="btn btn-primary bg-primary hover:opacity-90 border-0 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200" id="sp-use-btn">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        Use This Image
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ImageGeneration',

    data() {
        return {
            isGenerating: false,
            isUploading: false,
            promptText: '',
            generatedImage: null,
            selectedModel: 'basic',
            selectedSize: '1024x1024',
            selectedFormat: 'webp',
            customFields: localStorage.getItem('smartpress_custom_fields') 
                ? JSON.parse(localStorage.getItem('smartpress_custom_fields')) 
                : {},
            observerActive: false,
            buttonInjected: false,
            lastError: null,
            activeModal: null,
            activeContent: null,
            wpUser: window.smartpress_wpUserData ? window.smartpress_wpUserData : {}

        };
    },
    
    mounted() {
        this.observeMediaModal();
    },
    
    methods: {
        enhancePrompt() {
            const enhanceBtn = document.querySelector('#sp-enhance-btn');
            if (enhanceBtn) {
                // Update button text and disable it
                const btnSpan = enhanceBtn.querySelector('span');
                if (btnSpan) btnSpan.textContent = 'Enhancing...';
                enhanceBtn.classList.add('opacity-70');
                enhanceBtn.style.pointerEvents = 'none';
            }
            
            // Get the prompt input value
            const promptInput = document.querySelector('#sp-prompt-input');
            if (promptInput) {
                this.promptText = promptInput.value;
            }
            
            axios.post('https://67c9c3d498a9fb9b5174.10g8.cloud/enhance', {
                session: this.customFields.api_key,
                prompt: this.promptText,
                type: 'image'
            }).then(response => {
                console.log(response);
                this.promptText = response.data.text;
                
                // Update the input with enhanced text
                if (promptInput) {
                    promptInput.value = this.promptText;
                }
            }).catch(error => {
                console.error('Error enhancing prompt:', error);
            }).finally(() => {
                // Reset button state
                if (enhanceBtn) {
                    const btnSpan = enhanceBtn.querySelector('span');
                    if (btnSpan) btnSpan.textContent = 'Enhance';
                    enhanceBtn.classList.remove('opacity-70');
                    enhanceBtn.style.pointerEvents = '';
                }
            });
        },
        observeMediaModal() {
            // Create a mutation observer to detect when the media modal appears
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length) {
                        mutation.addedNodes.forEach((node) => {
                            try {
                                // Check if the added node is or contains the media modal
                                if (node.nodeType === 1) { // Element node
                                    // Check if the node itself is a media modal
                                    if (node.classList && node.classList.contains('media-modal')) {
                                        this.injectSmartPressButton(node);
                                    } 
                                    // Or check if it contains a media modal
                                    const modals = node.querySelectorAll('.media-modal');
                                    if (modals.length > 0) {
                                        modals.forEach(modal => this.injectSmartPressButton(modal));
                                    }
                                    
                                    // Specifically check for the media router which might load after the modal
                                    const routers = node.querySelectorAll('.media-router');
                                    if (routers.length > 0) {
                                        routers.forEach(router => {
                                            // Find the parent modal
                                            let parent = router;
                                            while (parent && !parent.classList.contains('media-modal')) {
                                                parent = parent.parentElement;
                                            }
                                            if (parent) {
                                                this.injectSmartPressButton(parent);
                                            }
                                        });
                                    }
                                }
                            } catch (error) {
                                this.lastError = `Observer error: ${error.message}`;
                                console.error('Observer error:', error);
                            }
                        });
                    }
                });
            });

            // Start observing the body element for added nodes
            observer.observe(document.body, { childList: true, subtree: true });
            this.observerActive = true;
            
            // Also check if the modal is already in the DOM
            this.checkExistingModal();
        },
        
        checkExistingModal() {
            // Check if a media modal already exists in the DOM
            const existingModals = document.querySelectorAll('.media-modal');
            if (existingModals.length > 0) {
                existingModals.forEach(modal => this.injectSmartPressButton(modal));
            }
        },
        
        injectSmartPressButton(modalElement) {
            try {
                // Find the media router (tab bar) in the modal
                const mediaRouter = modalElement.querySelector('.media-router');
                if (!mediaRouter) {
                    this.lastError = 'Media router not found in modal';
                    return;
                }
                
                // Create SmartPress button if it doesn't already exist
                if (!mediaRouter.querySelector('.smartpress-tab')) {
                    const smartPressButton = document.createElement('a');
                    smartPressButton.href = '#';
                    smartPressButton.className = 'media-menu-item smartpress-tab smart-press-button';
                    smartPressButton.innerHTML = 'SmartPress <span class="badge badge-sm badge-secondary">Beta</span>';
                    smartPressButton.setAttribute('role', 'tab');
                    smartPressButton.id = 'menu-item-smartpress';
                    smartPressButton.style.cssText = 'background-color: #eef; font-weight: bold;';
                    
                    // Add click event listener
                    smartPressButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        // Remove active class from all tabs
                        mediaRouter.querySelectorAll('.media-menu-item').forEach(item => {
                            item.classList.remove('active');
                        });
                        
                        // Add active class to SmartPress tab
                        smartPressButton.classList.add('active');
                        
                        // Store the active modal
                        this.activeModal = modalElement;
                        
                        // Show SmartPress content
                        this.showSmartPressContent(modalElement);
                    });
                    
                    // Also listen for other tab clicks to restore toolbar visibility
                    mediaRouter.querySelectorAll('.media-menu-item:not(.smartpress-tab)').forEach(tab => {
                        tab.addEventListener('click', () => {
                            this.handleMediaToolbarVisibility(modalElement, true);
                        });
                    });
                    
                    mediaRouter.appendChild(smartPressButton);
                    this.buttonInjected = true;
                }
            } catch (error) {
                this.lastError = `Injection error: ${error.message}`;
                console.error('Error injecting SmartPress button:', error);
            }
        },
        
        showSmartPressContent(modalElement) {
            try {
                // Find the media frame content area
                const contentArea = modalElement.querySelector('.media-frame-content');
                if (!contentArea) {
                    this.lastError = 'Media frame content not found';
                    return;
                }
                
                // Hide existing content
                Array.from(contentArea.children).forEach(child => {
                    child.style.display = 'none';
                });
                
                // Check if our content is already in the modal
                let smartPressContent = contentArea.querySelector('.smartpress-content');
                
                if (!smartPressContent) {
                    // Clone our template content and inject it into the modal
                    smartPressContent = this.$refs.smartPressContent.cloneNode(true);
                    smartPressContent.style.display = 'block';
                    contentArea.appendChild(smartPressContent);
                    
                    // Add event listeners manually since Vue binding doesn't work on cloned nodes
                    const generateBtn = smartPressContent.querySelector('#sp-generate-btn');
                    const promptInput = smartPressContent.querySelector('#sp-prompt-input');
                    const regenerateBtn = smartPressContent.querySelector('#sp-regenerate-btn');
                    const useBtn = smartPressContent.querySelector('#sp-use-btn');
                    const enhanceBtn = smartPressContent.querySelector('#sp-enhance-btn');
                    const resultImg = smartPressContent.querySelector('#sp-result-img');
                    const zoomImg = smartPressContent.querySelector('#sp-zoom-img');
                    const zoomContainer = smartPressContent.querySelector('.zoom-container');
                    
                    // Set up input for prompt
                    if (promptInput) {
                        promptInput.value = this.promptText;
                        promptInput.addEventListener('input', (e) => {
                            this.promptText = e.target.value;
                        });
                        
                        // Add enter key handler for the input
                        promptInput.addEventListener('keydown', (e) => {
                            if (e.key === 'Enter' && !this.isGenerating) {
                                this.handleGenerate(smartPressContent);
                            }
                        });
                    }
                    
                    // Set up enhance button handler
                    if (enhanceBtn) {
                        enhanceBtn.addEventListener('click', (e) => {
                            e.preventDefault();
                            
                            // Get the prompt input value from the modal
                            const promptText = promptInput ? promptInput.value : '';
                            
                            // Update button state
                            const btnSpan = enhanceBtn.querySelector('span');
                            if (btnSpan) btnSpan.textContent = 'Enhancing...';
                            enhanceBtn.classList.add('opacity-70');
                            enhanceBtn.style.pointerEvents = 'none';
                            
                            // Make the API call
                            axios.post('https://67c9c3d498a9fb9b5174.10g8.cloud/enhance', {
                                session: this.customFields.api_key,
                                prompt: promptText,
                                type: 'image'
                            }).then(response => {
                                console.log(response);
                                
                                // Update the input with enhanced text
                                if (promptInput && response.data && response.data.text) {
                                    promptInput.value = response.data.text;
                                    this.promptText = response.data.text;
                                }
                            }).catch(error => {
                                console.error('Error enhancing prompt:', error);
                            }).finally(() => {
                                // Reset button state
                                if (btnSpan) btnSpan.textContent = 'Enhance';
                                enhanceBtn.classList.remove('opacity-70');
                                enhanceBtn.style.pointerEvents = '';
                            });
                        });
                    }
                    
                    // Set up model selector
                    const modelSelect = smartPressContent.querySelector('#sp-model-select');
                    if (modelSelect) {
                        modelSelect.value = this.selectedModel;
                        modelSelect.addEventListener('change', (e) => {
                            this.selectedModel = e.target.value;
                        });
                        
                        // Set up dropdown behavior for model selector using unique ID
                        const modelDropdown = smartPressContent.querySelector('#sp-model-dropdown');
                        if (modelDropdown) {
                            const modelDropdownItems = modelDropdown.querySelectorAll('ul li a[data-value]');
                            const modelSelectedText = modelDropdown.querySelector('#sp-selected-model');
                            
                            modelDropdownItems.forEach(item => {
                                // Set initial selection
                                if (item.getAttribute('data-value') === this.selectedModel) {
                                    modelSelectedText.textContent = item.getAttribute('data-label');
                                }
                                
                                // Add click handler
                                item.addEventListener('click', (e) => {
                                    e.preventDefault();
                                    const value = e.target.getAttribute('data-value');
                                    const label = e.target.getAttribute('data-label');
                                    
                                    // Update the hidden select value
                                    modelSelect.value = value;
                                    this.selectedModel = value;
                                    
                                    // Update the displayed text
                                    modelSelectedText.textContent = label;
                                    
                                    // Trigger change event on the select
                                    modelSelect.dispatchEvent(new Event('change'));
                                });
                            });
                        }
                    }
                    
                    // Set up size selector
                    const sizeSelect = smartPressContent.querySelector('#sp-size-select');
                    if (sizeSelect) {
                        sizeSelect.value = this.selectedSize;
                        sizeSelect.addEventListener('change', (e) => {
                            this.selectedSize = e.target.value;
                        });
                        
                        // Set up dropdown behavior for size selector using unique ID
                        const sizeDropdown = smartPressContent.querySelector('#sp-size-dropdown');
                        if (sizeDropdown) {
                            const sizeDropdownItems = sizeDropdown.querySelectorAll('ul li a[data-value]');
                            const sizeSelectedText = sizeDropdown.querySelector('#sp-selected-size');
                            
                            sizeDropdownItems.forEach(item => {
                                // Set initial selection
                                if (item.getAttribute('data-value') === this.selectedSize) {
                                    sizeSelectedText.textContent = item.getAttribute('data-label');
                                }
                                
                                // Add click handler
                                item.addEventListener('click', (e) => {
                                    e.preventDefault();
                                    const value = e.target.getAttribute('data-value');
                                    const label = e.target.getAttribute('data-label');
                                    
                                    // Update the hidden select value
                                    sizeSelect.value = value;
                                    this.selectedSize = value;
                                    
                                    // Update the displayed text
                                    sizeSelectedText.textContent = label;
                                    
                                    // Trigger change event on the select
                                    sizeSelect.dispatchEvent(new Event('change'));
                                });
                            });
                        }
                    }
                    
                    // Set up format selector
                    const formatSelect = smartPressContent.querySelector('#sp-format-select');
                    if (formatSelect) {
                        formatSelect.value = this.selectedFormat;
                        formatSelect.addEventListener('change', (e) => {
                            this.selectedFormat = e.target.value;
                        });
                        
                        // Set up dropdown behavior for format selector using unique ID
                        const formatDropdown = smartPressContent.querySelector('#sp-format-dropdown');
                        if (formatDropdown) {
                            const formatDropdownItems = formatDropdown.querySelectorAll('ul li a[data-value]');
                            const formatSelectedText = formatDropdown.querySelector('#sp-selected-format');
                            
                            formatDropdownItems.forEach(item => {
                                // Set initial selection
                                if (item.getAttribute('data-value') === this.selectedFormat) {
                                    formatSelectedText.textContent = item.getAttribute('data-label');
                                }
                                
                                // Add click handler
                                item.addEventListener('click', (e) => {
                                    e.preventDefault();
                                    const value = e.target.getAttribute('data-value');
                                    const label = e.target.getAttribute('data-label');
                                    
                                    // Update the hidden select value
                                    formatSelect.value = value;
                                    this.selectedFormat = value;
                                    
                                    // Update the displayed text
                                    formatSelectedText.textContent = label;
                                    
                                    // Trigger change event on the select
                                    formatSelect.dispatchEvent(new Event('change'));
                                });
                            });
                        }
                    }
                    
                    // Set up generate button
                    if (generateBtn) {
                        generateBtn.addEventListener('click', () => {
                            this.handleGenerate(smartPressContent);
                        });
                    }
                    
                    // Set up regenerate button
                    if (regenerateBtn) {
                        regenerateBtn.addEventListener('click', () => {
                            this.handleGenerate(smartPressContent);
                        });
                    }
                    
                    // Set up use button
                    if (useBtn) {
                        useBtn.addEventListener('click', () => {
                            this.handleUseImage(smartPressContent);
                        });
                    }
                    
                    // Disable default behaviors of buttons inside the modal to prevent form submissions
                    smartPressContent.querySelectorAll('button').forEach(button => {
                        button.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        });
                    });
                    
                    // Setup image zoom functionality
                    if (resultImg) {
                        resultImg.addEventListener('mousemove', (e) => {
                            if (!zoomImg || !zoomContainer) return;
                            
                            // Show zoom container
                            zoomContainer.style.opacity = '1';
                            zoomImg.style.opacity = '1';
                            
                            // Get container dimensions
                            const rect = resultImg.getBoundingClientRect();
                            
                            // Calculate mouse position percentage
                            const xPercent = (e.clientX - rect.left) / rect.width;
                            const yPercent = (e.clientY - rect.top) / rect.height;
                            
                            // Update zoomed image source and position
                            if (zoomImg.src !== resultImg.src) {
                                zoomImg.src = resultImg.src;
                            }
                            
                            // Position the zoomed image
                            zoomImg.style.transform = `translate(-${xPercent * 50}%, -${yPercent * 50}%)`;
                        });
                        
                        resultImg.addEventListener('mouseleave', () => {
                            if (zoomContainer) {
                                zoomContainer.style.opacity = '0';
                                if (zoomImg) zoomImg.style.opacity = '0';
                            }
                        });
                    }
                } else {
                    // If it exists, just show it
                    smartPressContent.style.display = 'block';
                    
                    // Update prompt input with current value
                    const promptInput = smartPressContent.querySelector('#sp-prompt-input');
                    if (promptInput) {
                        promptInput.value = this.promptText;
                    }
                    
                    // Reset skeleton visibility based on whether we have an image
                    const skeleton = smartPressContent.querySelector('#sp-skeleton');
                    const resultContainer = smartPressContent.querySelector('#sp-result-container');
                    if (skeleton && resultContainer) {
                        if (this.generatedImage) {
                            skeleton.style.display = 'none';
                            resultContainer.style.display = 'block';
                        } else {
                            skeleton.style.display = 'block';
                            resultContainer.style.display = 'none';
                        }
                    }
                    
                    // Update model and size selectors with current values
                    const modelSelect = smartPressContent.querySelector('#sp-model-select');
                    if (modelSelect) {
                        modelSelect.value = this.selectedModel;
                        
                        // Update displayed text for model dropdown using ID
                        const modelDropdown = smartPressContent.querySelector('#sp-model-dropdown');
                        if (modelDropdown) {
                            const modelSelectedText = modelDropdown.querySelector('#sp-selected-model');
                            const selectedOption = modelDropdown.querySelector(`ul li a[data-value="${this.selectedModel}"]`);
                            if (modelSelectedText && selectedOption) {
                                modelSelectedText.textContent = selectedOption.getAttribute('data-label');
                            }
                        }
                    }
                    
                    const sizeSelect = smartPressContent.querySelector('#sp-size-select');
                    if (sizeSelect) {
                        sizeSelect.value = this.selectedSize;
                        
                        // Update displayed text for size dropdown using ID
                        const sizeDropdown = smartPressContent.querySelector('#sp-size-dropdown');
                        if (sizeDropdown) {
                            const sizeSelectedText = sizeDropdown.querySelector('#sp-selected-size');
                            const selectedOption = sizeDropdown.querySelector(`ul li a[data-value="${this.selectedSize}"]`);
                            if (sizeSelectedText && selectedOption) {
                                sizeSelectedText.textContent = selectedOption.getAttribute('data-label');
                            }
                        }
                    }
                    
                    const formatSelect = smartPressContent.querySelector('#sp-format-select');
                    if (formatSelect) {
                        formatSelect.value = this.selectedFormat;
                        
                        // Update displayed text for format dropdown using ID
                        const formatDropdown = smartPressContent.querySelector('#sp-format-dropdown');
                        if (formatDropdown) {
                            const formatSelectedText = formatDropdown.querySelector('#sp-selected-format');
                            const selectedOption = formatDropdown.querySelector(`ul li a[data-value="${this.selectedFormat}"]`);
                            if (formatSelectedText && selectedOption) {
                                formatSelectedText.textContent = selectedOption.getAttribute('data-label');
                            }
                        }
                    }
                    
                    // Update image if we have one
                    if (this.generatedImage) {
                        this.updateModalImage(smartPressContent, this.generatedImage);
                    }
                }
                
                // Store reference to the active content for later use
                this.activeContent = smartPressContent;
                
                // Disable media toolbar buttons that could conflict with our UI
                this.handleMediaToolbarVisibility(modalElement, false);
            } catch (error) {
                this.lastError = `Content error: ${error.message}`;
                console.error('Error showing SmartPress content:', error);
            }
        },
        
        async handleGenerate(modalContent = null) {
            // If we have a modal content, get the prompt text from the input field
            if (modalContent) {
                const promptInput = modalContent.querySelector('#sp-prompt-input');
                if (promptInput) {
                    this.promptText = promptInput.value;
                }
                
                // Get model selection
                const modelSelect = modalContent.querySelector('#sp-model-select');
                if (modelSelect) {
                    this.selectedModel = modelSelect.value;
                    
                    // Also update the displayed text
                    const modelDropdown = modalContent.querySelector('#sp-model-dropdown');
                    if (modelDropdown) {
                        const modelSelectedText = modelDropdown.querySelector('#sp-selected-model');
                        const selectedOption = modelDropdown.querySelector(`ul li a[data-value="${this.selectedModel}"]`);
                        if (modelSelectedText && selectedOption) {
                            modelSelectedText.textContent = selectedOption.getAttribute('data-label');
                        }
                    }
                }
                
                // Get size selection
                const sizeSelect = modalContent.querySelector('#sp-size-select');
                if (sizeSelect) {
                    this.selectedSize = sizeSelect.value;
                    
                    // Also update the displayed text
                    const sizeDropdown = modalContent.querySelector('#sp-size-dropdown');
                    if (sizeDropdown) {
                        const sizeSelectedText = sizeDropdown.querySelector('#sp-selected-size');
                        const selectedOption = sizeDropdown.querySelector(`ul li a[data-value="${this.selectedSize}"]`);
                        if (sizeSelectedText && selectedOption) {
                            sizeSelectedText.textContent = selectedOption.getAttribute('data-label');
                        }
                    }
                }
                
                // Get format selection
                const formatSelect = modalContent.querySelector('#sp-format-select');
                if (formatSelect) {
                    this.selectedFormat = formatSelect.value;
                    
                    // Also update the displayed text
                    const formatDropdown = modalContent.querySelector('#sp-format-dropdown');
                    if (formatDropdown) {
                        const formatSelectedText = formatDropdown.querySelector('#sp-selected-format');
                        const selectedOption = formatDropdown.querySelector(`ul li a[data-value="${this.selectedFormat}"]`);
                        if (formatSelectedText && selectedOption) {
                            formatSelectedText.textContent = selectedOption.getAttribute('data-label');
                        }
                    }
                }
            }
            
            if (!this.promptText.trim()) {
                alert('Please enter a description for the image you want to generate');
                return;
            }
            
            this.isGenerating = true;
            
            // Update UI in modal if provided
            if (modalContent) {
                const generateBtn = modalContent.querySelector('#sp-generate-btn');
                if (generateBtn) {
                    generateBtn.textContent = 'Generating...';
                    generateBtn.disabled = true;
                }
            }
            
            try {
                // Make the API call
                const response = await axios.post('https://67c9c3d498a9fb9b5174.10g8.cloud/generate-image', JSON.stringify({
                    session: this.customFields.api_key,
                    prompt: this.promptText,
                    outputFormat: this.selectedFormat,
                    model: this.selectedModel,
                    dimensions: this.selectedSize
                }), {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });
                if (response.data && response.data.image && response.data.image.base64) {
                    // Get the image data and format
                    const imageData = response.data.image.base64;
                    // Store the image format returned from the API
                    const imageFormat = response.data.image.format || this.selectedFormat;
                    this.generatedImage = imageData;
                    // Save the format for use during upload
                    this.selectedFormat = imageFormat;
                    
                    // Update UI in modal if provided
                    if (modalContent) {
                        this.updateModalImage(modalContent, this.generatedImage);
                    }
                } else {
                    throw new Error(response.data.error);
                }
            } catch (error) {
                console.error('Error generating image:', error);
                this.lastError = `Generation error: ${error.message}`;
                alert(`Failed to generate image, Please try again, Our system automatically detected and blocked this request because the generated image(s) may conflict our Responsible AI Policy.`);
            } finally {
                this.isGenerating = false;
                
                // Reset UI in modal if provided
                if (modalContent) {
                    const generateBtn = modalContent.querySelector('#sp-generate-btn');
                    if (generateBtn) {
                        generateBtn.textContent = 'Generate';
                        generateBtn.disabled = false;
                    }
                }
            }
        },
        
        handleRegenerate() {
            // Use the stored active content if available
            this.handleGenerate(this.activeContent);
        },
        
        updateModalImage(modalContent, imageUrl) {
            if (!modalContent) return;
            
            // Find the result container and image element
            const resultContainer = modalContent.querySelector('#sp-result-container');
            const imageElement = modalContent.querySelector('#sp-result-img');
            const zoomImgElement = modalContent.querySelector('#sp-zoom-img');
            const skeleton = modalContent.querySelector('#sp-skeleton');
            
            if (resultContainer && imageElement) {
                // Update the image source
                imageElement.src = imageUrl;
                
                // Update the zoom image source
                if (zoomImgElement) {
                    zoomImgElement.src = imageUrl;
                }
                
                // Show the result container
                resultContainer.style.display = 'block';
                
                // Hide the skeleton
                if (skeleton) {
                    skeleton.style.display = 'none';
                }
            }
        },
        
        async handleUseImage(modalContent = null) {
            if (!this.generatedImage) {
                alert('No image available to use. Please generate an image first.');
                return;
            }
            
            this.isUploading = true;
            
            // Update UI in modal if provided
            if (modalContent) {
                const useBtn = modalContent.querySelector('#sp-use-btn');
                if (useBtn) {
                    useBtn.textContent = 'Uploading...';
                    useBtn.disabled = true;
                }
            }
            
            try {
                // Upload the image and get the result
                const result = await this.uploadToWordPress(this.generatedImage);
                
                if (result.success) {
                    // Reset UI 
                    this.promptText = '';
                    this.generatedImage = null;
                    
                    // Reset UI in modal if provided
                    if (modalContent) {
                        const resultContainer = modalContent.querySelector('#sp-result-container');
                        const promptInput = modalContent.querySelector('#sp-prompt-input');
                        const skeleton = modalContent.querySelector('#sp-skeleton');
                        
                        if (resultContainer) {
                            resultContainer.style.display = 'none';
                        }
                        
                        if (skeleton) {
                            skeleton.style.display = 'block';
                        }
                        
                        if (promptInput) {
                            promptInput.value = '';
                        }
                    }
                    
                    // If we're in the SmartPress tab, switch to the media library tab
                    if (this.activeModal) {
                        const activeTab = this.activeModal.querySelector('.media-router .active');
                        if (activeTab && activeTab.classList.contains('smartpress-tab')) {
                            // Find the "Media Library" tab and click it
                            const libraryTab = this.activeModal.querySelector('.media-router .media-menu-item:not(.smartpress-tab)');
                            if (libraryTab) {
                                libraryTab.click();
                            }
                        }
                    }
                    
                    // Restore toolbar visibility
                    this.handleMediaToolbarVisibility(this.activeModal, true);
                    
                    // We no longer need to auto-insert the image
                } else {
                    throw new Error(result.error || 'Unknown error');
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                alert('Failed to upload image: ' + error.message);
            } finally {
                this.isUploading = false;
                
                // Reset UI in modal if provided
                if (modalContent) {
                    const useBtn = modalContent.querySelector('#sp-use-btn');
                    if (useBtn) {
                        useBtn.textContent = 'Use This Image';
                        useBtn.disabled = false;
                    }
                }
            }
        },
        
        async uploadToWordPress(imageUrl) {
            try {
                // Create a sanitized filename from the prompt text
                const sanitizedPrompt = this.promptText
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '')
                    .substring(0, 40);
                
                // Use the format from the API response if available, otherwise use the selected format
                let imageFormat = this.selectedFormat;
                
                // If the image URL already includes the format information, extract it
                if (imageUrl.includes('data:image/')) {
                    const formatMatch = imageUrl.match(/data:image\/([^;]+);/);
                    if (formatMatch && formatMatch[1]) {
                        imageFormat = formatMatch[1];
                    }
                }
                
                const filename = sanitizedPrompt ? `${sanitizedPrompt}.${imageFormat}` : `smartpress-image.${imageFormat}`;
                
                // Check if the image data starts with "data:image/" - if not, add proper headers
                let imageDataToUpload = imageUrl;
                if (!imageDataToUpload.startsWith('data:image/')) {
                    imageDataToUpload = `data:image/${imageFormat};base64,${imageDataToUpload}`;
                }
                

                let baseUrl = this.wpUser.site_url;

                // Always use the data URL endpoint
                const response = await fetch(baseUrl + '/wp-json/smartpress/v1/upload-data-url', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.smartpress_vars?.nonce || ''
                    },
                    body: JSON.stringify({
                        data_url: imageDataToUpload,
                        filename: filename
                    })
                });
                
                if (!response.ok) {
                    throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                if (data.success && data.id) {
                    // Wait a moment for WordPress to process the image before selecting it
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // Trigger refresh of the media library
                    this.refreshMediaLibrary();
                    
                    // Try to select the media in the modal - this will now persist selection
                    const selected = this.selectMediaInModal(data.id);
                    
                    return {
                        success: true,
                        id: data.id,
                        url: data.url,
                        selected: selected
                    };
                } else {
                    throw new Error(data.message || 'Unknown error');
                }
            } catch (error) {
                return {
                    success: false,
                    error: error.message
                };
            }
        },
        
        refreshMediaLibrary() {
            try {
                if (typeof window === 'undefined' || !window.wp || !window.wp.media || !window.wp.media.frame) {
                    return false;
                }
                
                const frame = window.wp.media.frame;
                
                // If we have a frame and it has a content area with a library
                if (frame && frame.content && frame.content.get) {
                    const library = frame.content.get().collection;
                    if (library && typeof library.props.get === 'function') {
                        // Get current query parameters
                        const props = library.props.get('query');
                        
                        // Reset the query to force refresh
                        library.props.set('query', false);
                        
                        // Restore the original query
                        library.props.set('query', props);
                        
                        // Tell the library to refresh/reload
                        if (typeof library.refresh === 'function') {
                            library.refresh();
                        }
                    }
                }
                
                return true;
            } catch (error) {
                console.error('Error refreshing media library:', error);
                return false;
            }
        },
        
        selectMediaInModal(mediaId) {
            try {
                // Check if wp global object is available
                if (typeof window === 'undefined' || !window.wp || !window.wp.media || !window.wp.media.frame) {
                    return false;
                }
                
                const frame = window.wp.media.frame;
                
                // If we're in a media modal, select our newly uploaded attachment
                if (frame && frame.state && typeof frame.state === 'function') {
                    const state = frame.state();
                    
                    if (state && state.get && typeof state.get === 'function') {
                        const selection = state.get('selection');
                        
                        if (selection) {
                            try {
                                const attachment = window.wp.media.attachment(mediaId);
                                
                                if (!attachment) {
                                    return false;
                                }
                                
                                // Fetch the attachment details
                                attachment.fetch();
                                
                                // Clear existing selection to prevent multiple items being selected
                                // REMOVED THIS LINE TO PREVENT DESELECTION: selection.reset();
                                
                                // Add it to the selection
                                selection.add(attachment);
                                
                                // If we're in the SmartPress tab, switch to library view
                                // but maintain the selection
                                if (this.activeModal) {
                                    const activeTab = this.activeModal.querySelector('.media-router .active');
                                    if (activeTab && activeTab.classList.contains('smartpress-tab')) {
                                        // Find the "Media Library" tab and click it
                                        const libraryTab = this.activeModal.querySelector('.media-router .media-menu-item:not(.smartpress-tab)');
                                        if (libraryTab) {
                                            // Store the selection before switching tabs
                                            const selectedId = mediaId;
                                            
                                            // Switch to library tab
                                            libraryTab.click();
                                            
                                            // Ensure selection persists after tab switch
                                            setTimeout(() => {
                                                this.highlightMediaItem(frame, selectedId);
                                            }, 300);
                                        }
                                    }
                                }
                                
                                // Switch to the Library view while preserving selection
                                if (frame.content && frame.content.mode && typeof frame.content.mode === 'function') {
                                    const previousSelection = selection.toJSON();
                                    frame.content.mode('browse');
                                    
                                    // Re-apply selection after mode change, if needed
                                    setTimeout(() => {
                                        if (selection.length === 0 && previousSelection.length > 0) {
                                            previousSelection.forEach(item => {
                                                selection.add(window.wp.media.attachment(item.id));
                                            });
                                        }
                                    }, 100);
                                }
                                
                                // Try to highlight the media item in the grid
                                this.highlightMediaItem(frame, mediaId);
                                
                                return true;
                            } catch (error) {
                                console.error('Error working with selection:', error);
                                return false;
                            }
                        }
                    }
                }
                
                return false;
            } catch (error) {
                console.error('Error selecting media in modal:', error);
                return false;
            }
        },
        
        highlightMediaItem(frame, mediaId) {
            if (!frame || !frame.$el) return;
            
            // Give WordPress a moment to render the newly added attachment
            setTimeout(() => {
                try {
                    // Find the media item in the grid
                    const mediaItem = frame.$el.find(`li.attachment[data-id="${mediaId}"]`);
                    
                    if (mediaItem.length) {
                        // Highlight it without deselecting other items
                        if (!mediaItem.hasClass('selected')) {
                            mediaItem.click();
                        }
                        
                        // Ensure the item remains visible by scrolling to it
                        mediaItem[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                        
                        // Add additional visual indicator
                        mediaItem.css('box-shadow', '0 0 5px 2px #0073aa');
                        setTimeout(() => {
                            mediaItem.css('box-shadow', '');
                        }, 2000);
                    } else {
                        // If item wasn't found, try again after a longer delay
                        // This helps with the set featured image modal which might take longer to refresh
                        setTimeout(() => {
                            const mediaItem = frame.$el.find(`li.attachment[data-id="${mediaId}"]`);
                            if (mediaItem.length) {
                                if (!mediaItem.hasClass('selected')) {
                                    mediaItem.click();
                                }
                                mediaItem[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 1500);
                    }
                } catch (error) {
                    console.error('Error highlighting media item:', error);
                }
            }, 800);
        },
        
        handleMediaToolbarVisibility(modalElement, show = true) {
            if (!modalElement) return;
            
            try {
                // Find all toolbar elements that might interfere with our UI
                const toolbarElements = modalElement.querySelectorAll('.media-toolbar-primary, .media-toolbar-secondary');
                
                toolbarElements.forEach(toolbar => {
                    if (show) {
                        toolbar.style.display = '';
                    } else {
                        toolbar.style.display = 'none';
                    }
                });
            } catch (error) {
                console.error('Error handling media toolbar visibility:', error);
            }
        }
    }
};
</script>

<style scoped>
/* Styles for the futuristic UI elements */
.zoom-container {
    z-index: 10;
    background-color: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.prompt-area {
    background-image: linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), 
                     url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cpath fill='%23f0f9ff' d='M30,1.5c-16.542,0-30,13.458-30,30.001c0,16.541,13.458,29.999,30,29.999s30-13.458,30-29.999C60,14.958,46.542,1.5,30,1.5z M30,50.5c-10.492,0-19.001-8.509-19.001-19.001S19.508,12.499,30,12.499s19,8.509,19,18.999S40.492,50.5,30,50.5z'/%3E%3C/svg%3E");
}

/* Override default focus styles */
.prompt-area:focus {
    outline: none !important;
    border-color: var(--primary-color, #4f46e5) !important;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2) !important;
}

.prompt-area:focus-visible {
    outline: none !important;
    border-color: var(--primary-color, #4f46e5) !important;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2) !important;
}

/* Add zoom transition */
.zoom-image {
    transition: transform 0.1s ease-out;
}
</style>